
import { MapPin, Bed, Bath, Home, Calendar, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatAddressForDisplay, getSecondaryLocationInfo } from "@/utils/addressPrivacy";

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url?: string;
  created_at: string;
}

interface PublicPropertyCardProps {
  property: Property;
}

const PublicPropertyCard = ({ property }: PublicPropertyCardProps) => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(property.price);

  const daysOnSite = formatDistanceToNow(new Date(property.created_at), { addSuffix: false });

  const displayAddress = formatAddressForDisplay(
    {
      address: property.address,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code
    },
    isAuthenticated,
    isAuthenticated
  );

  const secondaryLocation = getSecondaryLocationInfo({
    address: property.address,
    city: property.city,
    state: property.state,
    zip_code: property.zip_code
  });

  return (
    <Link to={`/public/seller/${sellerId}/property/${property.id}`} className="block">
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
        <div className="relative aspect-video overflow-hidden">
          <img
            src={property.image_url || "/placeholder.svg"}
            alt={`Property at ${property.address}`}
            className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <p className="text-white font-bold text-lg">{formattedPrice}</p>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="flex items-start gap-2 mb-4">
            <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium leading-tight">{displayAddress}</h3>
                {!isAuthenticated && (
                  <Lock className="h-3 w-3 text-muted-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {isAuthenticated ? secondaryLocation : `${property.city}, ${property.state} ${property.zip_code}`}
              </p>
              {!isAuthenticated && (
                <p className="text-xs text-godyspo-gold mt-1">
                  Sign up to view full address
                </p>
              )}
            </div>
          </div>
          
          <div className="flex justify-between text-sm mb-4">
            <div className="flex items-center">
              <Bed className="h-4 w-4 mr-1" />
              <span>{property.bedrooms} {property.bedrooms === 1 ? 'Bed' : 'Beds'}</span>
            </div>
            <div className="flex items-center">
              <Bath className="h-4 w-4 mr-1" />
              <span>{property.bathrooms} {property.bathrooms === 1 ? 'Bath' : 'Baths'}</span>
            </div>
            <div className="flex items-center">
              <Home className="h-4 w-4 mr-1" />
              <span>{property.square_feet.toLocaleString()} Sq Ft</span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-border text-xs text-muted-foreground flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            <span>Listed {daysOnSite} ago</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default PublicPropertyCard;
